/**
 * Permissão exigida por cada tela do painel administrativo.
 *
 * Até a auditoria de setembro/2026 as rotas do painel só checavam o TIPO do
 * usuário: qualquer colaborador abria qualquer endereço digitando a URL, e a
 * tela carregava — só as chamadas de API é que eram recusadas, quando eram.
 * Este mapa é o espelho do que o backend exige em `routes/api.php`.
 *
 * Vale só para o COLABORADOR: administrador passa por cima de tudo, e empresa
 * e parceiros nem chegam neste painel (o tipo já os manda para o painel deles).
 */

/** Padrões em ORDEM: o primeiro que casar manda. `:param` casa um segmento. */
const REGRAS: Array<[string, string]> = [
  // --- Comercial ---
  ['/commercial/subscribers', 'commercial.subscribers.view'],
  ['/commercial/companies', 'commercial.companies.view'],
  ['/plans', 'commercial.plans.view'],
  ['/commercial/discount-coupons', 'commercial.discount_coupons.view'],
  ['/commercial/proposals/kanban', 'commercial.proposals.view'],
  ['/commercial/proposals', 'commercial.proposals.view'],
  ['/commercial/reports/general', 'commercial.reports.general'],
  ['/commercial/reports/providers', 'commercial.reports.providers'],
  ['/commercial/reports/age', 'commercial.reports.age'],
  ['/commercial/reports/location', 'commercial.reports.location'],
  ['/commercial/reports', 'commercial.reports.general'],

  // --- Financeiro ---
  ['/revenues/projection', 'financial.projection.view'],
  ['/revenues', 'financial.revenues.view'],
  ['/financial/duplicate-invoices', 'financial.duplicate_invoices.view'],
  ['/financial/reports/earnings', 'financial.reports.earnings'],
  ['/financial/reports/commissions', 'financial.reports.commissions'],
  ['/financial/reports', 'financial.reports.earnings'],

  // --- Atendimentos ---
  ['/medical-appointments', 'medical_appointments.appointments.view'],
  ['/support/inbox', 'support.chat.view'],
  ['/support/settings', 'support.teams.view'],

  // --- Credenciamento ---
  ['/credentialing/providers', 'credentialing.providers.view'],
  ['/credentialing/kanban', 'credentialing.providers.view'],
  ['/credentialing/specialty-search', 'credentialing.providers.specialty_search'],
  ['/credentialing/reports/dashboard', 'credentialing.reports.dashboard'],
  ['/credentialing/reports/commissions', 'credentialing.reports.commissions'],
  ['/credentialing/reports/map', 'credentialing.reports.map'],
  ['/credentialing/dashboard', 'credentialing.providers.dashboards'],

  // --- Links ---
  ['/catalog', 'links.catalog.view'],

  // --- Administração ---
  ['/administractive/hierarchies', 'administractive.hierarchies.view'],
  ['/administractive/users', 'administractive.users.view'],

  // --- Configurações (o índice abre para quem tem qualquer uma) ---
  ['/settings/parametros', 'settings.parameters.view'],
  ['/settings/workflow/banks', 'settings.banks.view'],
  ['/settings/workflow/marital-statuses', 'settings.marital_statuses.view'],
  ['/settings/workflow/degree-relationships', 'settings.degree_relationships.view'],
  ['/settings/workflow/specialties', 'workflow.specialties.view'],
  ['/settings/workflow/tests', 'workflow.tests.view'],
  ['/settings/commercial/service-providers', 'settings.service_providers.view'],
  ['/settings/commercial/report-templates', 'settings.report_templates.view'],
  ['/settings/commercial/proposal-pricing', 'settings.proposal_pricing.view'],
  ['/settings/commercial/proposal-setup', 'settings.proposal_setup.view'],
  ['/settings/commercial/proposal-catalog', 'settings.proposal_setup.view'],
  ['/settings/commercial/proposal-templates', 'settings.proposal_templates.view'],
  ['/settings/commercial/proposal-stages', 'commercial.proposal_stages.view'],
  ['/settings/commercial/services', 'settings.services.view'],
  ['/settings/commercial/manual-blocks', 'settings.manual_blocks.view'],
  ['/settings/commercial/products', 'commercial.products.view'],
  ['/settings/commercial/commission-structures', 'commercial.commission_structures.view'],
  ['/settings/commercial/modalities', 'commercial.modalities.view'],
  ['/settings/credentialing/types', 'credentialing.types.view'],
  ['/settings/credentialing/steps', 'credentialing.steps.view'],
  ['/settings/credentialing/commission-structures', 'credentialing.commission_structures.view'],
  ['/settings/support/whatsapp', 'settings.support_whatsapp.view'],
  ['/settings/integrations/medicar', 'settings.integrations_medicar.view'],

  // --- Integrações ---
  ['/integrations/medicar', 'integrations.medicar.view'],
  ['/integrations/backups', 'integrations.backups.view'],
  ['/integrations/asaas/ungenerated', 'integrations.asaas.ungenerated_view'],
  ['/integrations/asaas/charge-finder', 'integrations.asaas.charge_finder'],
  ['/integrations/error-logs', 'integrations.error_logs.view'],
]

/**
 * A permissão que abre esta tela, ou `null` quando a tela não é gateada
 * (dashboard inicial, índice de configurações e redirecionamentos).
 *
 * O casamento é por prefixo de segmento: `/commercial/subscribers/123/editar`
 * cai na mesma regra de `/commercial/subscribers`. As regras mais específicas
 * vêm antes na lista.
 */
export function permissionForPath(pathname: string): string | null {
  const limpo = pathname.replace(/\/+$/, '') || '/'
  for (const [prefixo, permissao] of REGRAS) {
    if (limpo === prefixo || limpo.startsWith(prefixo + '/')) return permissao
  }
  return null
}
